import React, { useState, useMemo } from 'react';
import { Search, Filter, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

/**
 * Reusable DataTable Component
 * Provides consistent design, sorting, pagination, search, and filtering
 * Supports both table view (desktop) and card view (tablet/mobile)
 * 
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Column configuration
 * @param {Function} props.renderRow - Function to render each table row
 * @param {Function} props.renderCard - Function to render each card (optional, for mobile view)
 * @param {String} props.searchPlaceholder - Placeholder for search input
 * @param {Function} props.searchFilter - Custom search filter function
 * @param {Object} props.filters - Additional filter configuration
 * @param {React.Node} props.emptyState - Custom empty state component
 * @param {Number} props.defaultItemsPerPage - Default items per page (default: 5)
 * @param {Array} props.itemsPerPageOptions - Options for items per page
 * @param {Boolean} props.loading - Loading state
 * @param {String} props.error - Error message
 */
const DataTable = ({
    data = [],
    columns = [],
    renderRow,
    renderCard,
    searchPlaceholder = 'Search...',
    searchFilter,
    filters,
    emptyState,
    defaultItemsPerPage = 5,
    itemsPerPageOptions = [5, 10, 15, 20, 25, 50],
    loading = false,
    error = null,
    // Server-side pagination props
    serverSide = false,
    totalElements = 0,
    totalPages: serverTotalPages = 0,
    currentPage: serverCurrentPage = 1,
    onPageChange,
    onSearch,
    onSort,
    onItemsPerPageChange,
    searchTerm: externalSearchTerm,
    viewMode = 'auto', // 'auto', 'table', or 'grid'
}) => {
    const [internalSearchTerm, setInternalSearchTerm] = useState('');
    const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
    const setSearchTerm = externalSearchTerm !== undefined ? (val) => onSearch && onSearch(val) : setInternalSearchTerm;
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [showItemsPerPageDropdown, setShowItemsPerPageDropdown] = useState(false);

    // Detect screen size for responsive view - show table for tablets (min-width: 768px) and desktops
    const isTabletOrDesktop = useMediaQuery('(min-width: 768px)');

    // Sorting handler
    const handleSortClick = (field) => {
        if (!field) return;

        let newDir = 'asc';
        if (sortField === field) {
            newDir = sortDirection === 'asc' ? 'desc' : 'asc';
        }

        setSortField(field);
        setSortDirection(newDir);

        if (serverSide && onSort) {
            onSort(field, newDir);
        }
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (!field) return null;

        if (sortField !== field) {
            return <ArrowUpDown size={14} className="text-gray-400" />;
        }
        return sortDirection === 'asc'
            ? <ArrowUp size={14} className="text-blue-600" />
            : <ArrowDown size={14} className="text-blue-600" />;
    };

    // Filter and sort data
    const filteredAndSortedData = useMemo(() => {
        if (serverSide) return data;
        let result = [...data];

        // Apply search filter
        if (searchTerm && searchFilter) {
            result = result.filter(item => searchFilter(item, searchTerm));
        }

        // Apply sorting
        if (sortField) {
            result.sort((a, b) => {
                let aValue = a[sortField];
                let bValue = b[sortField];

                // Handle null/undefined
                if (aValue == null) return 1;
                if (bValue == null) return -1;

                // Handle string comparison (case-insensitive)
                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (sortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                } else {
                    return aValue < bValue ? 1 : -1;
                }
            });
        }

        return result;
    }, [data, searchTerm, searchFilter, sortField, sortDirection, serverSide]);

    // Pagination
    const totalPages = serverSide ? serverTotalPages : Math.ceil(filteredAndSortedData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        if (serverSide) return data;
        const startIndex = ((serverSide ? serverCurrentPage : currentPage) - 1) * itemsPerPage;
        return filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredAndSortedData, currentPage, itemsPerPage, serverSide, data, serverCurrentPage]);

    // Reset to page 1 when filters change (client-side only)
    React.useEffect(() => {
        if (!serverSide) {
            setCurrentPage(1);
        }
    }, [searchTerm, itemsPerPage, serverSide]);

    // Handle Page Click
    const handlePageClick = (page) => {
        if (serverSide && onPageChange) {
            onPageChange(page);
        } else {
            setCurrentPage(page);
        }
    };

    // Render pagination buttons with smart ellipsis
    const renderPaginationButtons = () => {
        const buttons = [];
        const maxVisiblePages = 5;
        const current = serverSide ? serverCurrentPage : currentPage;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total is small
            for (let i = 1; i <= totalPages; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => handlePageClick(i)}
                        className={`px-3 py-1.5 text-xs rounded font-medium transition-all ${current === i
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                            }`}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            // Show ellipsis for large page counts
            const showEllipsisStart = current > 3;
            const showEllipsisEnd = current < totalPages - 2;

            // Always show first page
            buttons.push(
                <button
                    key={1}
                    onClick={() => handlePageClick(1)}
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-all ${current === 1
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                        }`}
                >
                    1
                </button>
            );

            if (showEllipsisStart) {
                buttons.push(<span key="ellipsis-start" className="px-2 text-gray-500">...</span>);
            }

            // Show pages around current page
            const start = Math.max(2, current - 1);
            const end = Math.min(totalPages - 1, current + 1);

            for (let i = start; i <= end; i++) {
                buttons.push(
                    <button
                        key={i}
                        onClick={() => handlePageClick(i)}
                        className={`px-3 py-1.5 text-xs rounded font-medium transition-all ${current === i
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                            }`}
                    >
                        {i}
                    </button>
                );
            }

            if (showEllipsisEnd) {
                buttons.push(<span key="ellipsis-end" className="px-2 text-gray-500">...</span>);
            }

            // Always show last page
            buttons.push(
                <button
                    key={totalPages}
                    onClick={() => handlePageClick(totalPages)}
                    className={`px-3 py-1.5 text-xs rounded font-medium transition-all ${current === totalPages
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-gray-300 hover:bg-gray-100 text-gray-700'
                        }`}
                >
                    {totalPages}
                </button>
            );
        }

        return buttons;
    };

    // Render loading skeleton for cards
    const renderCardSkeleton = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
            {Array(itemsPerPage).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="h-3 bg-gray-200 rounded w-full"></div>
                        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden max-w-full flex flex-col h-full">
            {/* Search and Filter Bar */}
            <div className="p-2 border-b border-gray-100 flex flex-col md:flex-row gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        className="w-full pl-8 pr-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        value={searchTerm}
                        onChange={(e) => {
                            const val = e.target.value;
                            setSearchTerm(val);
                            // Parent handle the threshold, but we can pass everything if needed
                            if (serverSide && onSearch) {
                                onSearch(val);
                            }
                        }}
                    />
                    {searchTerm && (
                        <button 
                            onClick={() => {
                                setSearchTerm('');
                                if (onSearch) onSearch('');
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            aria-label="Clear search"
                        >
                            <X size={12} />
                        </button>
                    )}
                </div>
                {filters && (
                    <div className="flex gap-2">
                        <button className="p-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition-all">
                            <Filter size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Table or Card View */}
            {(viewMode === 'table' || (viewMode === 'auto' && isTabletOrDesktop)) ? (
                /* Desktop: Table View */
                <div className="flex-1 overflow-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 text-gray-500 text-[10px] uppercase tracking-wider sticky top-0">
                            <tr>
                                {columns.map((column, index) => {
                                    // Determine column alignment and width
                                    const isActionsColumn = column.header === 'ACTIONS' || column.header === 'Actions';
                                    const isCheckboxColumn = typeof column.header !== 'string';
                                    const alignClass = isActionsColumn ? 'text-right' : '';
                                    const widthClass = isCheckboxColumn ? 'w-12' : '';

                                    return (
                                        <th
                                            key={index}
                                            className={`px-3 py-2 font-semibold whitespace-nowrap ${alignClass} ${widthClass}`}
                                        >
                                            {column.sortable ? (
                                                <button
                                                    onClick={() => handleSortClick(column.field)}
                                                    className="flex items-center gap-2 hover:text-blue-600 transition-colors"
                                                >
                                                    {column.header}
                                                    {getSortIcon(column.field)}
                                                </button>
                                            ) : (
                                                column.header
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                // Loading skeleton
                                Array(itemsPerPage).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        {columns.map((_, colIndex) => (
                                            <td key={colIndex} className="px-3 py-2">
                                                <div className="h-4 bg-gray-200 rounded w-full"></div>
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : error ? (
                                // Error state
                                <tr>
                                    <td colSpan={columns.length} className="px-3 py-6 text-center">
                                        <div className="flex flex-col items-center gap-2 text-red-500">
                                            <p className="font-medium">{error}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : paginatedData.length > 0 ? (
                                // Data rows
                                paginatedData.map((item, index) => renderRow(item, index))
                            ) : (
                                // Empty state
                                <tr>
                                    <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500">
                                        {emptyState || 'No data found matching your search.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* Mobile/Tablet: Card View */
                <div className="flex-1 overflow-auto p-3 pb-24 md:pb-3 custom-scrollbar">
                    {loading ? (
                        renderCardSkeleton()
                    ) : error ? (
                        <div className="text-center py-12">
                            <div className="flex flex-col items-center gap-2 text-red-500">
                                <p className="font-medium">{error}</p>
                            </div>
                        </div>
                    ) : paginatedData.length > 0 ? (
                        renderCard ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {paginatedData.map((item, index) => (
                                    <div key={index}>
                                        {renderCard(item, index)}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">
                                <p>Card view not implemented. Please provide renderCard function.</p>
                            </div>
                        )
                    ) : (
                        <div className="text-center py-12">
                            {emptyState || <p className="text-gray-500">No data found matching your search.</p>}
                        </div>
                    )}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 0 && (
                <div className="border-t border-gray-100 px-2 py-1.5 bg-gray-50 flex items-center justify-between">
                    {/* Items per page selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-600">Show:</span>
                        <div className="relative">
                            <button
                                onClick={() => setShowItemsPerPageDropdown(!showItemsPerPageDropdown)}
                                className="flex items-center gap-1 px-2 py-1 text-[10px] bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-medium min-w-[50px]"
                            >
                                {itemsPerPage}
                                <ChevronDown size={12} className={`transition-transform ${showItemsPerPageDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showItemsPerPageDropdown && (
                                <div className="absolute bottom-full mb-1 left-0 w-24 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                                    {itemsPerPageOptions.map(option => (
                                        <button
                                            key={option}
                                            onClick={() => {
                                                setItemsPerPage(option);
                                                setShowItemsPerPageDropdown(false);
                                                if (serverSide) {
                                                    if (onItemsPerPageChange) onItemsPerPageChange(option);
                                                }
                                            }}
                                            className={`w-full text-center px-2 py-1 text-[10px] transition-colors ${itemsPerPage === option
                                                ? 'bg-blue-50 text-blue-600 font-semibold'
                                                : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <span className="text-[10px] text-gray-600">
                            of {serverSide ? totalElements : filteredAndSortedData.length} items
                        </span>
                    </div>

                    {/* Pagination controls */}
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageClick(Math.max(1, (serverSide ? serverCurrentPage : currentPage) - 1))}
                            disabled={(serverSide ? serverCurrentPage : currentPage) === 1}
                            className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            ‹
                        </button>

                        {renderPaginationButtons()}

                        <button
                            onClick={() => handlePageClick(Math.min(totalPages, (serverSide ? serverCurrentPage : currentPage) + 1))}
                            disabled={(serverSide ? serverCurrentPage : currentPage) === totalPages}
                            className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            ›
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;
